import { platform } from "../platform/index.js";
// Binance public market-data endpoints — genuinely free, real-time, and
// served with permissive CORS; no key, no account. Used by the free
// aggregate for crypto symbols, making the free tier's crypto data LIVE
// while equities/futures stay delayed.

import { MarketDataError, type OHLCPoint } from "../market-data.js";
import { rangeToMs, intervalToMs } from "./types.js";

/** "BTC-USD" (our Yahoo-style ticker) → "BTCUSDT" (Binance spot). Pure. */
export function binanceSymbol(symbol: string): string | null {
  const match = /^([A-Z0-9]{2,10})-USD$/.exec(symbol.trim().toUpperCase());
  if (!match) return null;
  return `${match[1]}USDT`;
}

/** Our interval vocabulary → Binance kline interval. Pure. */
export function binanceInterval(interval: string): string | null {
  const map: Record<string, string> = {
    "1m": "1m",
    "5m": "5m",
    "15m": "15m",
    "30m": "30m",
    "60m": "1h",
    "90m": "1h",
    "1h": "1h",
    "1d": "1d",
    "1wk": "1w",
    "1mo": "1M",
  };
  return map[interval] ?? null;
}

type Kline = [number, string, string, string, string, string, ...unknown[]];

export async function binanceCandles(symbol: string, range: string, interval: string): Promise<{ candles: OHLCPoint[]; currentPrice?: number }> {
  const pair = binanceSymbol(symbol);
  const kInterval = binanceInterval(interval);
  if (!pair || !kInterval) throw new MarketDataError(`Binance can't serve ${symbol} @ ${interval}`);

  const end = Date.now();
  const start = end - rangeToMs(range);
  const limit = Math.min(1000, Math.max(10, Math.ceil(rangeToMs(range) / intervalToMs(interval)) + 2));
  const url = `https://api.binance.com/api/v3/klines?symbol=${pair}&interval=${kInterval}&startTime=${start}&endTime=${end}&limit=${limit}`;

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 15_000);
  try {
    const response = await platform.dataFetch(url, { signal: controller.signal });
    if (!response.ok) throw new MarketDataError(`Binance klines failed: ${response.status}`);
    const klines = (await response.json()) as Kline[];
    const candles: OHLCPoint[] = klines.map((k) => ({
      date: new Date(k[0]),
      open: Number(k[1]),
      high: Number(k[2]),
      low: Number(k[3]),
      close: Number(k[4]),
      volume: Number(k[5]),
    }));
    const last = candles[candles.length - 1];
    return { candles, currentPrice: last?.close };
  } finally {
    clearTimeout(timeout);
  }
}
